// Blueprint: crap/blueprint/host-navigation/architecture/001-route-navigate-linkout.md
import type { NavigateTarget } from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import appManager from '@app/appManager';
import AuthModule from '@modules/AuthModule';
import type { IContactCenterTenant } from '@modules/AuthModule/lib/types';
import formatTenantLink from '@lib/utils/formatTenantLink';
import {
  ROUTE_TYPE,
  resolveNavigateUrl,
  resolveRouteNavigateTarget,
} from '@lib/utils/resolveNavigateUrl';

const logger = appManager.logger('openNavigateTarget');

/**
 * Shared resolve/open helper every host wiring the agent-inbox MFEs'
 * `onNavigate` delegates to (CustomerMfeAdapter, InboxTextsPage, ...) —
 * collapses the byte-identical `resolve → null-check → resetAuthAge →
 * window.open` bodies those hosts used to duplicate.
 *
 * Route-typed targets resolve through the same `formatTenantLink` builder
 * every other monolith link-out uses (tenantLinks.ts) rather than a separate
 * BFF round trip, so the URL shape and redirect behavior are identical to
 * every existing link-out (REQ-NAV-ROUTE-001 AC-3).
 */
export function openNavigateTarget(
  target: NavigateTarget,
  tenants: ReadonlyArray<IContactCenterTenant>,
): void {
  const url =
    target.type === ROUTE_TYPE
      ? resolveRouteNavigateTargetUrl(target)
      : resolveNavigateUrl(target, tenants);

  if (!url) {
    return;
  }
  AuthModule.resetAuthAge();
  window.open(url, '_blank', 'noopener,noreferrer');
}

function resolveRouteNavigateTargetUrl(target: NavigateTarget): string | null {
  const resolved = resolveRouteNavigateTarget(target);
  if (!resolved) {
    // Malformed/unusable Route target: a provider bug, not a CSR-actionable
    // failure, so no toast — just the first actual observability on this
    // path (ARCH-NAV-ROUTE-001 Failure Modes).
    logger.exception(
      new Error(
        'resolveRouteNavigateTarget returned null for a Route NavigateTarget',
      ),
      'Route link-out target unusable',
      {
        route: 'route' in target ? target.route : undefined,
        tenantId: 'tenantId' in target ? target.tenantId : undefined,
      },
    );
    return null;
  }
  return formatTenantLink(resolved.tenantId, resolved.path);
}
